/**
 * Gateway ops counters: request volume by route/status, keys issued, and
 * active-session gauges. Served by the control plane (GET /api/v1/metrics, via
 * controlApi — direct, JWT), which reads the SAME shared Redis the @aes gateway
 * publishes into. The portal never calls @aes/@mgmt for this. Shape is
 * byte-compatible with @aes metrics.Snapshot. `serviceId` only scopes the cache.
 */
import { useQuery } from '@tanstack/react-query';
import { controlApi } from '@/api/client';
import { aesKeys } from './_shared';

export interface AesMetricsSnapshot {
  uptime_seconds: number;
  requests: {
    total: number;
    by_route: Record<string, number>;
    by_status: Record<string, number>;
  };
  keys_issued: {
    total: number;
    by_class: Record<string, number>;
  };
  sessions: {
    opened: Record<string, number>;
    revoked: Record<string, number>;
    active: Record<string, number>;
  };
}

export function useAesMetrics(serviceId: string | undefined, refreshMs = 15_000) {
  return useQuery<AesMetricsSnapshot>({
    queryKey: aesKeys.metrics(serviceId ?? ''),
    enabled: !!serviceId,
    refetchInterval: refreshMs,
    staleTime: 5_000,
    retry: false,
    queryFn: async () => (await controlApi.get<AesMetricsSnapshot>('/metrics')).data,
  });
}

/** Sum the values of a counter map (e.g. total 5xx across statuses). */
export function sumBy(map: Record<string, number> | undefined, predicate?: (k: string) => boolean): number {
  if (!map) return 0;
  return Object.entries(map).reduce((acc, [k, v]) => (!predicate || predicate(k) ? acc + v : acc), 0);
}
